import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Buildings, Calendar, CurrencyEur, Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { supabase, offerStartsAt, offerCurrentPrice } from "../lib/supabase";

const fmt = (d) => d ? d.toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const today = () => new Date().toISOString().slice(0, 10);

const CATS = [
  { v: "yoga", l: "Yoga" },
  { v: "pilates", l: "Pilates" },
  { v: "fisioterapia", l: "Fisioterapia" },
  { v: "gym", l: "Gym" },
  { v: "padel", l: "Pádel" },
  { v: "crossfit", l: "CrossFit" },
  { v: "spinning", l: "Spinning" },
];

const emptyBusiness = { name: "", category: "yoga", address: "", description: "", image_url: "" };
const emptyOffer = {
  business_id: "",
  title: "",
  description: "",
  date: today(),
  start_time: "18:00",
  original_price: 20,
  offer_price: 10,
  available_slots: 8,
  total_slots: 10,
  status: "active",
  image_url: "",
};

const AdminDashboard = () => {
  const [tab, setTab] = useState("offers");
  const [offers, setOffers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  // form dialogs
  const [bizForm, setBizForm] = useState(null);    // null | { ...business }
  const [offerForm, setOfferForm] = useState(null); // null | { ...offer }

  const reload = async () => {
    setLoading(true);
    const [a, b, biz] = await Promise.all([
      supabase.from("offers").select("*, business:businesses(*)").order("date", { ascending: false }).limit(200),
      supabase.from("bookings").select("*, offer:offers(*, business:businesses(*)), payment:payments(*)").order("created_at", { ascending: false }).limit(50),
      supabase.from("businesses").select("*").order("name", { ascending: true }).limit(100),
    ]);
    setOffers(a.data || []);
    setBookings(b.data || []);
    setBusinesses(biz.data || []);
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const revenue = useMemo(() => bookings
    .filter((b) => b.status === "confirmed" || b.status === "used")
    .reduce((s, b) => s + Number(b.payment?.[0]?.amount ?? offerCurrentPrice(b.offer) ?? 0), 0), [bookings]);

  // ── Business CRUD ────────────────────────────────────────────────
  const saveBusiness = async (e) => {
    e.preventDefault();
    if (!bizForm.name?.trim()) { toast.error("Nombre requerido"); return; }
    const payload = { ...bizForm };
    delete payload.created_at;
    let q;
    if (payload.id) q = supabase.from("businesses").update(payload).eq("id", payload.id);
    else { delete payload.id; q = supabase.from("businesses").insert(payload); }
    const { error } = await q;
    if (error) { toast.error(error.message); return; }
    toast.success(payload.id ? "Business actualizado" : "Business creado");
    setBizForm(null);
    reload();
  };

  const deleteBusiness = async (id) => {
    if (!window.confirm("¿Eliminar este business? Las ofertas asociadas pueden quedar huérfanas.")) return;
    const { error } = await supabase.from("businesses").delete().eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Business eliminado"); reload(); }
  };

  // ── Offer CRUD ───────────────────────────────────────────────────
  const saveOffer = async (e) => {
    e.preventDefault();
    if (!offerForm.business_id) { toast.error("Selecciona un business"); return; }
    if (!offerForm.title?.trim()) { toast.error("Título requerido"); return; }
    const payload = { ...offerForm };
    ["original_price", "offer_price", "available_slots", "total_slots"].forEach((k) => {
      if (payload[k] !== undefined && payload[k] !== "") payload[k] = Number(payload[k]);
    });
    delete payload.created_at;
    delete payload.business; // join field
    let q;
    if (payload.id) q = supabase.from("offers").update(payload).eq("id", payload.id);
    else { delete payload.id; q = supabase.from("offers").insert(payload); }
    const { error } = await q;
    if (error) { toast.error(error.message); return; }
    toast.success(payload.id ? "Oferta actualizada" : "Oferta creada");
    setOfferForm(null);
    reload();
  };

  const deleteOffer = async (id) => {
    if (!window.confirm("¿Eliminar esta oferta?")) return;
    const { error } = await supabase.from("offers").delete().eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Oferta eliminada"); reload(); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-12" data-testid="admin-dashboard">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
        <span className="text-[11px] uppercase tracking-[0.3em] text-sage-deep block mb-2">/ panel admin</span>
        <h1 className="font-heading text-4xl md:text-5xl font-light text-forest">Gestiona el catálogo</h1>
        <p className="text-stone mt-2 text-sm">Los admins pueden crear, editar y eliminar businesses y ofertas globalmente.</p>
      </motion.div>

      <div className="grid sm:grid-cols-4 gap-3 mb-10">
        <Stat icon={<Buildings size={20} />} l="Businesses" v={businesses.length} />
        <Stat icon={<Calendar size={20} />} l="Ofertas" v={offers.length} />
        <Stat icon={<Calendar size={20} />} l="Reservas" v={bookings.length} />
        <Stat icon={<CurrencyEur size={20} />} l="Ingresos" v={`${revenue.toFixed(2)}€`} />
      </div>

      <div className="flex gap-2 mb-6 border-b border-forest/10">
        {[
          { v: "offers",     l: "Ofertas" },
          { v: "businesses", l: "Businesses" },
          { v: "bookings",   l: "Reservas" },
        ].map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`admin-tab-${t.v}`}
            className={`px-4 py-3 text-xs uppercase tracking-[0.2em] border-b-2 -mb-px transition ${tab === t.v ? "border-sage-deep text-sage-deep" : "border-transparent text-stone hover:text-forest"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-stone text-sm">Cargando…</p>
      ) : (<>
        {tab === "offers" && (
          <section className="space-y-3">
            <div className="flex justify-between items-center">
              <h2 className="text-xs uppercase tracking-[0.3em] text-sage-deep">/ Ofertas</h2>
              <button onClick={() => setOfferForm({ ...emptyOffer, business_id: businesses[0]?.id || "" })}
                data-testid="new-offer-btn"
                className="inline-flex items-center gap-2 bg-sage-deep text-paper px-4 py-2 text-xs uppercase tracking-wider rounded-full hover:bg-forest transition">
                <Plus size={14} /> Nueva oferta
              </button>
            </div>
            {offers.length === 0 ? (
              <p className="text-stone text-sm py-8">Sin ofertas. Crea la primera con el botón de arriba.</p>
            ) : offers.map((o) => (
              <div key={o.id} data-testid={`offer-row-${o.id}`} className="border border-forest/10 bg-white rounded-xl p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="flex-1">
                  <div className="font-heading text-lg font-semibold text-forest">{o.title}</div>
                  <div className="text-sm text-stone">
                    {o.business?.name} · {fmt(offerStartsAt(o))} · {o.available_slots}/{o.total_slots} plazas · estado: {o.status}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sage-deep font-semibold">{offerCurrentPrice(o).toFixed(2)}€</div>
                  {o.original_price && Number(o.original_price) > offerCurrentPrice(o) && (
                    <div className="text-ash text-xs line-through">{Number(o.original_price).toFixed(2)}€</div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setOfferForm({ ...o })} data-testid={`edit-offer-${o.id}`} className="p-2 border border-forest/15 hover:border-sage-deep hover:text-sage-deep rounded-md transition"><PencilSimple size={16} /></button>
                  <button onClick={() => deleteOffer(o.id)} data-testid={`del-offer-${o.id}`} className="p-2 border border-forest/15 hover:border-red-500 hover:text-red-500 rounded-md transition"><Trash size={16} /></button>
                </div>
              </div>
            ))}
          </section>
        )}

        {tab === "businesses" && (
          <section className="space-y-3">
            <div className="flex justify-between items-center">
              <h2 className="text-xs uppercase tracking-[0.3em] text-sage-deep">/ Businesses</h2>
              <button onClick={() => setBizForm({ ...emptyBusiness })} data-testid="new-business-btn"
                className="inline-flex items-center gap-2 bg-sage-deep text-paper px-4 py-2 text-xs uppercase tracking-wider rounded-full hover:bg-forest transition">
                <Plus size={14} /> Nuevo business
              </button>
            </div>
            {businesses.length === 0 ? (
              <p className="text-stone text-sm py-8">Sin businesses. Crea el primero con el botón de arriba.</p>
            ) : businesses.map((b) => (
              <div key={b.id} data-testid={`biz-row-${b.id}`} className="border border-forest/10 bg-white rounded-xl p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <div className="flex-1">
                  <div className="font-medium text-forest">{b.name}</div>
                  <div className="text-sm text-stone">{b.category} · {b.address || "sin dirección"}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setBizForm({ ...b })} data-testid={`edit-biz-${b.id}`} className="p-2 border border-forest/15 hover:border-sage-deep hover:text-sage-deep rounded-md transition"><PencilSimple size={16} /></button>
                  <button onClick={() => deleteBusiness(b.id)} data-testid={`del-biz-${b.id}`} className="p-2 border border-forest/15 hover:border-red-500 hover:text-red-500 rounded-md transition"><Trash size={16} /></button>
                </div>
              </div>
            ))}
          </section>
        )}

        {tab === "bookings" && (
          <section className="space-y-3">
            <h2 className="text-xs uppercase tracking-[0.3em] text-sage-deep">/ Reservas recibidas</h2>
            {bookings.length === 0 ? (
              <p className="text-stone text-sm py-8">Aún sin reservas.</p>
            ) : bookings.slice(0, 30).map((b) => (
              <div key={b.id} className="border border-forest/10 bg-white rounded-xl p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
                <div className="flex-1">
                  <div className="font-medium text-forest">{b.offer?.title || "—"}</div>
                  <div className="text-sm text-stone">{b.email_guest || b.user_id || "—"} · {b.offer?.business?.name}</div>
                </div>
                <div className="text-sm text-stone">{fmt(offerStartsAt(b.offer))}</div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                  b.status === "confirmed" ? "bg-sage/20 text-sage-deep" :
                  b.status === "used" ? "bg-lavender/30 text-forest" :
                  b.status === "expired" ? "bg-ash/30 text-stone" : "bg-peach/30 text-forest"
                }`}>{b.status}</span>
              </div>
            ))}
          </section>
        )}
      </>)}

      {bizForm && (
        <Modal title={bizForm.id ? "Editar business" : "Nuevo business"} onClose={() => setBizForm(null)}>
          <form onSubmit={saveBusiness} className="space-y-4" data-testid="business-form">
            <FormRow label="Nombre">
              <input required value={bizForm.name} onChange={(e) => setBizForm({ ...bizForm, name: e.target.value })} className={inputCls} data-testid="biz-name" />
            </FormRow>
            <FormRow label="Categoría">
              <select value={bizForm.category} onChange={(e) => setBizForm({ ...bizForm, category: e.target.value })} className={inputCls} data-testid="biz-category">
                {CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </FormRow>
            <FormRow label="Dirección">
              <input value={bizForm.address || ""} onChange={(e) => setBizForm({ ...bizForm, address: e.target.value })} className={inputCls} data-testid="biz-address" />
            </FormRow>
            <FormRow label="Descripción">
              <textarea rows={3} value={bizForm.description || ""} onChange={(e) => setBizForm({ ...bizForm, description: e.target.value })} className={inputCls} data-testid="biz-description" />
            </FormRow>
            <FormRow label="Image URL">
              <input value={bizForm.image_url || ""} onChange={(e) => setBizForm({ ...bizForm, image_url: e.target.value })} className={inputCls} placeholder="https://…" data-testid="biz-image" />
            </FormRow>
            <div className="flex justify-end gap-2 pt-3">
              <button type="button" onClick={() => setBizForm(null)} className="px-5 py-2.5 text-xs uppercase tracking-wider border border-forest/15 rounded-full text-stone hover:bg-mist">Cancelar</button>
              <button type="submit" data-testid="biz-submit" className="px-5 py-2.5 text-xs uppercase tracking-wider bg-sage-deep text-paper rounded-full hover:bg-forest">Guardar</button>
            </div>
          </form>
        </Modal>
      )}

      {offerForm && (
        <Modal title={offerForm.id ? "Editar oferta" : "Nueva oferta"} onClose={() => setOfferForm(null)}>
          <form onSubmit={saveOffer} className="space-y-4" data-testid="offer-form">
            <FormRow label="Business">
              <select required value={offerForm.business_id || ""} onChange={(e) => setOfferForm({ ...offerForm, business_id: e.target.value })} className={inputCls} data-testid="offer-business">
                <option value="">— Selecciona —</option>
                {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </FormRow>
            <FormRow label="Título">
              <input required value={offerForm.title} onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })} className={inputCls} data-testid="offer-title" />
            </FormRow>
            <FormRow label="Descripción">
              <textarea rows={2} value={offerForm.description || ""} onChange={(e) => setOfferForm({ ...offerForm, description: e.target.value })} className={inputCls} data-testid="offer-description" />
            </FormRow>
            <div className="grid grid-cols-2 gap-3">
              <FormRow label="Fecha">
                <input required type="date" value={offerForm.date || ""} onChange={(e) => setOfferForm({ ...offerForm, date: e.target.value })} className={inputCls} data-testid="offer-date" />
              </FormRow>
              <FormRow label="Hora inicio">
                <input required type="time" value={(offerForm.start_time || "").slice(0, 5)} onChange={(e) => setOfferForm({ ...offerForm, start_time: e.target.value })} className={inputCls} data-testid="offer-time" />
              </FormRow>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormRow label="Precio original (€)">
                <input required type="number" min="0" step="0.01" value={offerForm.original_price} onChange={(e) => setOfferForm({ ...offerForm, original_price: e.target.value })} className={inputCls} data-testid="offer-orig" />
              </FormRow>
              <FormRow label="Precio oferta (€)">
                <input required type="number" min="0" step="0.01" value={offerForm.offer_price} onChange={(e) => setOfferForm({ ...offerForm, offer_price: e.target.value })} className={inputCls} data-testid="offer-price" />
              </FormRow>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormRow label="Plazas disponibles">
                <input required type="number" min="0" value={offerForm.available_slots} onChange={(e) => setOfferForm({ ...offerForm, available_slots: e.target.value })} className={inputCls} data-testid="offer-avail" />
              </FormRow>
              <FormRow label="Plazas totales">
                <input required type="number" min="0" value={offerForm.total_slots} onChange={(e) => setOfferForm({ ...offerForm, total_slots: e.target.value })} className={inputCls} data-testid="offer-total" />
              </FormRow>
            </div>
            <FormRow label="Estado">
              <select value={offerForm.status || "active"} onChange={(e) => setOfferForm({ ...offerForm, status: e.target.value })} className={inputCls} data-testid="offer-status">
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </FormRow>
            <FormRow label="Image URL">
              <input value={offerForm.image_url || ""} onChange={(e) => setOfferForm({ ...offerForm, image_url: e.target.value })} className={inputCls} placeholder="https://…" data-testid="offer-image" />
            </FormRow>
            <div className="flex justify-end gap-2 pt-3">
              <button type="button" onClick={() => setOfferForm(null)} className="px-5 py-2.5 text-xs uppercase tracking-wider border border-forest/15 rounded-full text-stone hover:bg-mist">Cancelar</button>
              <button type="submit" data-testid="offer-submit" className="px-5 py-2.5 text-xs uppercase tracking-wider bg-sage-deep text-paper rounded-full hover:bg-forest">Guardar</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

const inputCls = "w-full bg-white border border-forest/15 px-4 py-2.5 text-sm focus:border-sage-deep focus:outline-none rounded-md";

const FormRow = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.2em] text-stone block mb-2">{label}</span>
    {children}
  </label>
);

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="admin-modal">
    <div className="absolute inset-0 bg-forest/40 backdrop-blur-sm" onClick={onClose} />
    <motion.div initial={{ opacity: 0, scale: 0.96, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
      className="relative bg-paper rounded-2xl border border-forest/10 shadow-2xl max-w-lg w-full max-h-[90vh] overflow-auto">
      <div className="flex items-center justify-between p-5 border-b border-forest/10 sticky top-0 bg-paper">
        <h3 className="font-heading text-xl font-semibold text-forest">{title}</h3>
        <button onClick={onClose} className="p-1 hover:bg-mist rounded-md" data-testid="modal-close"><X size={18} /></button>
      </div>
      <div className="p-5">{children}</div>
    </motion.div>
  </div>
);

const Stat = ({ icon, l, v }) => (
  <div className="border border-forest/10 bg-white rounded-2xl p-5">
    <div className="text-sage-deep mb-2">{icon}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-stone">{l}</div>
    <div className="font-heading text-2xl font-semibold mt-1 text-forest">{v}</div>
  </div>
);

export default AdminDashboard;
